<template>
    <div class="input-group no-select mb-3" :class="{ 'has-label': label }">
        <label v-if="label" :for="id">{{ label }}</label>
        <input :id="id"
               :ref="id"
               type="text"
               :value="_value"
               :class="{append: copyField}"
               :maxlength="maxLength || 999"
               :placeholder="placeholder"
               @blur="onBlur"
               @focus="onFocus"
               @keydown="onKeyDown"
               :autofocus="autofocus"
               :readonly="true"/>
        <transition name="slide">
            <div v-if="showOptions"
                 class="select-options fade-in">
                <div v-for="option in options"
                     class="option"
                     :class="{active: selectedOption?.[valKey] === option[valKey]}"
                     :key="option[valKey]"
                     @mousedown="setOption(option)">
                    {{ option[nameKey] }}
                </div>
            </div>
        </transition>
    </div>
</template>

<script lang="ts">
import {Component, Prop, Vue, Watch} from "vue-property-decorator";
import {KeyValueDictionaryItem} from "@/interfaces/entities/KeyValueDictionaryItem";
import {KeyNameDictionaryItem} from "@/interfaces/entities/KeyNameDictionaryItem";

type OptionItem<ValueType> = KeyNameDictionaryItem<ValueType> | KeyValueDictionaryItem<ValueType>;

@Component
export default class SelectField<ValueType> extends Vue {
    @Prop({type: Boolean, default: false}) autofocus!: boolean;
    @Prop({type: String, default: ""}) placeholder!: string;
    @Prop({type: String}) label!: string;
    @Prop() init!: string | number;
    @Prop({required: true}) options!: Array<OptionItem<ValueType>>;
    @Prop({required: true}) valKey!: keyof OptionItem<ValueType>;
    @Prop({required: true}) nameKey!: keyof OptionItem<ValueType>;
    @Prop({type: Boolean, default: false}) required!: boolean;
    @Prop({type: String, default: "text"}) type!: string;
    @Prop({type: Number, default: 0}) maxLength!: number;
    @Prop({type: Boolean, default: false}) copyField!: boolean;
    @Prop() value!: string | number;

    private id: string = Math.random().toString(36).substr(2, 5);
    private showOptions: boolean = false;

    private selectedOption: OptionItem<ValueType> | null = null;

    get _value(): string | number {
        return this.selectedOption?.[this.nameKey] || "";
    }

    created() {
        this.initializeSelection(this.options);
    }

    mounted() {
        if (this.autofocus) {
            this.$nextTick(() => {
                (this.$refs[this.id] as HTMLInputElement)?.focus();
            });
        }
    }

    @Watch("options")
    optionsChanged(to: Array<KeyValueDictionaryItem<ValueType> | KeyNameDictionaryItem<ValueType>>, from: Array<KeyValueDictionaryItem<ValueType> | KeyNameDictionaryItem<ValueType>>) {
        if ((!from || from.length === 0) && to && to.length > 0) {
            this.initializeSelection(to);
        }
    }

    private initializeSelection(options: Array<OptionItem<ValueType>>) {
        if (!options || options.length === 0) {
            this.setOption(undefined);
            return;
        }

        if (this.init !== undefined && this.init !== null && this.init !== "") {
            const initialOption = options.find((option: OptionItem<ValueType>) => {
                return option[this.valKey as keyof OptionItem<ValueType>] === this.init;
            });

            this.setOption(initialOption ?? options[0]);
            return;
        }

        this.setOption(options[0]);
    }

    public setOption(option?: OptionItem<ValueType>) {
        this.selectedOption = option ?? null;
        this.$emit("change", this.selectedOption?.[this.valKey]);
    }

    private onFocus() {
        this.showOptions = true;
        this.$emit("focus");
    }

    private onBlur() {
        this.$emit("blur");
        this.$nextTick(() => this.showOptions = false);
    }

    private onKeyDown(e: KeyboardEvent) {
        if (this.options.length === 0) return;

        if (e.key === "Enter") {
            if (this.showOptions) e.preventDefault();
            this.$nextTick(() => this.showOptions = false);
            this.$emit("enter-pressed");
        }

        //  Choose the next or previous item from the
        //  options dropdown on arrow press
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            let index = this.options.findIndex((option: OptionItem<ValueType>) => {
                return option[this.valKey] === this.selectedOption?.[this.valKey];
            });
            index = (index + (e.key === "ArrowUp" ? -1 : 1)) % this.options.length;
            if (index < 0) index += this.options.length;
            this.setOption(this.options[index]);
        }
    }
}
</script>

<style lang="scss" scoped>
.slide-enter-active {
    transition: transform .2s ease-out, opacity .1s ease-in .1s;
}

.slide-enter {
    opacity: 0;
    transform: translateY(-10vh);
}

.slide-leave-to {
    opacity: 0;
}

.input-group {
    position: relative;

    label {
        @include fontCaption();
        margin-bottom: 3px;
    }

    &:after {
        content: "";
        display: block;
        height: 16px;
        width: 16px;
        background: transparent url("../../assets/icons/dropdown-blob-midgrey.svg") no-repeat center;
        background-size: 12px 12px;
        position: absolute;
        right: 8px;
        top: 12px;
        pointer-events: none;
    }

    &.has-label {
        &:after {
            top: 32px;
        }
    }

    input {
        height: 40px;
        background-color: $greyShade1;
        border-color: $greyShade3;
        border-style: solid;
        border-width: 1px;
        border-radius: 3px;
        width: 100%;
        padding: 10px 8px 12px;
        font-family: "FFMark W04", "Helvetica Neue", Arial, sans-serif;
        font-size: 16px;
        font-weight: 500;
        flex: 1;
        text-transform: capitalize;

        &:hover {
            cursor: pointer;
        }

        &:focus {
            box-shadow: 0 0 6px $greyShade3;
            outline: none;
        }

        &[readonly] {
            color: $fullGrey;
        }

        &::selection {
            background-color: $greyShade3;
        }
    }

    .select-options {
        position: absolute;
        width: 100%;
        height: fit-content;
        background-color: white;
        max-height: 240px;
        overflow-y: auto;
        border: 1px solid $greyShade3;
        box-shadow: 0 4px 10px 0 rgba(0, 0, 0, 0.19);
        top: 38px;
        z-index: 5;

        .option {
            padding: 5px 30px 5px 10px;
            text-transform: capitalize;

            &:not(:first-child) {
                border-top: 1px solid $greyShade3;
            }

            &.active,
            &:hover {
                background-color: $highlightColorShade1;
                color: $highlightColor;
                cursor: pointer;
            }
        }
    }

    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    input[type=number] {
        -moz-appearance: textfield;
    }
}

@media only screen and (max-width: $breakPoint - 1px) {
    .input-group {
        label {
            font-size: 12px;
        }

        input {
            height: 44px;
        }

        .input-group-append {
            width: 44px;
            height: 44px;
            background-size: 20px 20px;
        }
    }
}
</style>
